import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react'

import styled from 'styled-components'
import Joi from 'joi'
import toast from 'react-hot-toast'

import RadioButtonGroup from '../../../components/RadioButtonGroup'
import { StandardInput } from '../../../components/Input'
import {
  SingleSelect,
  CreatableSelectComponent,
} from '../../../components/Select'

import apiClient from '../../../helpers/apiClient'

import { getFromLocalStorage } from '../../../utils/util'

const PaymentAndBenefits = forwardRef((props, ref) => {
  const [payBasis, setPayBasis] = useState('Per Month')
  const [currency, setCurrency] = useState('PHP')
  const [minPayment, setMinPayment] = useState(0)
  const [maxPayment, setMaxPayment] = useState(0)
  const [benefits, setBenefits] = useState([])
  const [fieldErrors, setFieldErrors] = useState({})

  const [currencyList, setCurrencyList] = useState([])

  const paymentAndBenefits = getFromLocalStorage('paymentAndBenefits')

  const validationSchema = useMemo(() => {
    return Joi.object({
      currency: Joi.string().label('Currency').required(),
      minPayment: Joi.number()
        .label('Min Payment')
        .required()
        .less(Joi.ref('maxPayment'))
        .messages({
          'number.less': '"Min Payment" must be less than "Max Payment"',
        }),

      maxPayment: Joi.number().label('Max Payment').required(),
      benefits: Joi.array()
        .items(
          Joi.object({
            value: Joi.string().required(),
            label: Joi.string().required(),
          })
        )
        .min(1)
        .label('Benefits')
        .required()
        .messages({
          'array.min': 'Please select at least one benefit.',
          'any.required': 'Benefits selection is required.',
        }),
    }).custom((obj, helpers) => {
      if (obj.minPayment >= obj.maxPayment) {
        return helpers.message('"Min Payment" must be less than "Max Payment"')
      }
      return obj
    })
  }, [])

  useEffect(() => {
    setPayBasis(paymentAndBenefits?.payBasis || 'Per Month')
    setCurrency(paymentAndBenefits?.currency || '')
    setMinPayment(paymentAndBenefits?.minPayment || '')
    setMaxPayment(paymentAndBenefits?.maxPayment || '')
    setBenefits(
      (paymentAndBenefits && JSON.parse(paymentAndBenefits?.benefits)) || []
    )

    apiClient
      .get('/currency')
      .then((resp) => {
        const result = resp?.data?.resp
        const allCurrencies = Object.keys(result).map((cur) => ({
          value: cur,
          label: cur,
        }))
        setCurrencyList(allCurrencies)
      })
      .catch((err) => console.log(err))
  }, [])

  const handleNext = () => {
    const formData = {
      payBasis,
      currency,
      minPayment,
      maxPayment,
      benefits: JSON.stringify(benefits),
    }

    const forValidation = {
      currency: currency.label,
      minPayment,
      maxPayment,
      benefits,
    }

    const result = validationSchema.validate(forValidation, {
      abortEarly: false,
    })

    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)

      toast.error('Please fix the validation errors to continue.')

      return false
    }

    localStorage.setItem('paymentAndBenefits', JSON.stringify(formData))

    return true
  }

  useImperativeHandle(ref, () => ({
    onNext: handleNext,
  }))

  return (
    <StyledPaymentAndBenefits>
      <RadioButtonGroup
        title="Pay Basis"
        list={[
          {
            value: 'Per Month',
            label: 'Per Month',
          },
          {
            value: 'Per Year',
            label: 'Per Year',
          },
        ]}
        selected={payBasis}
        onChange={setPayBasis}
      />
      <SingleSelect
        options={currencyList}
        selected={currency}
        placeholder="Select Currency"
        onChange={setCurrency}
        wrapperClass={fieldErrors.currency && 'error'}
        subText={fieldErrors.currency}
      />

      <StandardInput
        type="number"
        title="Min. Payment"
        value={minPayment}
        onChange={setMinPayment}
        required
        wrapperClass={fieldErrors.minPayment && 'error'}
        subText={fieldErrors.minPayment}
      />
      <StandardInput
        type="number"
        title="Max. Payment"
        value={maxPayment}
        onChange={setMaxPayment}
        required
        wrapperClass={fieldErrors.maxPayment && 'error'}
        subText={fieldErrors.maxPayment}
      />

      <CreatableSelectComponent
        noOption
        id="benefits"
        title="Benefits"
        selected={benefits}
        onChange={setBenefits}
        placeholder="Type something and press enter..."
        wrapperClass={fieldErrors.benefits && 'error'}
        subText={fieldErrors.benefits}
      />
    </StyledPaymentAndBenefits>
  )
})

const StyledPaymentAndBenefits = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  width: 100%;
`

PaymentAndBenefits.displayName = 'Payment and Benefits'

export default PaymentAndBenefits
